import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Alert,
  Button,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  Memory as MemoryIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import healthService from '../services/healthService';

/**
 * Componente per visualizzare lo status del sistema BeSocial
 * Mostra informazioni su FeathersJS, MySQL, memoria e uptime
 */
const SystemStatus = ({ autoRefresh = false, refreshInterval = 30000 }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const loadStatus = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await healthService.checkStatus();
      
      if (result.success) {
        setStatus(result.data);
        setLastUpdate(new Date());
      } else {
        setError(result.error);
        setStatus(null);
      }
    } catch (err) {
      setError(err.message);
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();

    if (autoRefresh) {
      const stopMonitoring = healthService.startMonitoring((result) => {
        if (result.success) {
          setStatus(result.data);
          setLastUpdate(new Date());
          setError(null);
        } else {
          setError(result.error);
        }
      }, refreshInterval);

      return () => stopMonitoring();
    }
  }, [autoRefresh, refreshInterval]);

  const formatUptime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours}h ${minutes}m ${secs}s`;
  };

  if (loading && !status) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" p={3}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Caricamento status sistema...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error && !status) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h6">Errore di connessione</Typography>
            <Typography variant="body2">{error}</Typography>
          </Alert>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadStatus}
            fullWidth
          >
            Riprova
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" component="div">
            System Status
          </Typography>
          <Box>
            <Chip
              icon={status?.overall === 'healthy' ? <CheckCircleIcon /> : <ErrorIcon />}
              label={status?.overall === 'healthy' ? 'HEALTHY' : 'UNHEALTHY'}
              color={status?.overall === 'healthy' ? 'success' : 'error'}
              sx={{ mr: 1 }}
            />
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadStatus}
              disabled={loading}
            >
              Aggiorna
            </Button>
          </Box>
        </Box>

        {lastUpdate && (
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            Ultimo aggiornamento: {lastUpdate.toLocaleTimeString()}
          </Typography>
        )}

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          {/* FeathersJS Status */}
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center" mb={1}>
              <SpeedIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">FeathersJS</Typography>
            </Box>
            <Box pl={4}>
              <Typography variant="body2" color="text.secondary">
                Status: {' '}
                <Chip
                  size="small"
                  icon={status?.feathersjs?.status === 'ok' ? <CheckCircleIcon /> : <ErrorIcon />}
                  label={status?.feathersjs?.status === 'ok' ? 'OK' : 'ERROR'}
                  color={status?.feathersjs?.status === 'ok' ? 'success' : 'error'}
                />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Versione: {status?.feathersjs?.version}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Uptime: {status?.uptime ? formatUptime(status.uptime) : 'N/A'}
              </Typography>
            </Box>
          </Grid>

          {/* Database MySQL Status */}
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center" mb={1}>
              <StorageIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Database MySQL</Typography>
            </Box>
            <Box pl={4}>
              <Typography variant="body2" color="text.secondary">
                Status: {' '}
                <Chip
                  size="small"
                  icon={status?.database?.connected ? <CheckCircleIcon /> : <ErrorIcon />}
                  label={status?.database?.connected ? 'CONNESSO' : 'DISCONNESSO'}
                  color={status?.database?.connected ? 'success' : 'error'}
                />
              </Typography>
              {status?.database?.version && (
                <Typography variant="body2" color="text.secondary">
                  Versione: {status.database.version}
                </Typography>
              )}
              {status?.database?.name && (
                <Typography variant="body2" color="text.secondary">
                  Database: {status.database.name}
                </Typography>
              )}
              {status?.database?.error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {status.database.error}
                </Alert>
              )}
            </Box>
          </Grid>

          {/* Memory Status */}
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center" mb={1}>
              <MemoryIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Memoria</Typography>
            </Box>
            <Box pl={4}>
              <Typography variant="body2" color="text.secondary">
                Usata: {status?.memory?.used} {status?.memory?.unit}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Totale: {status?.memory?.total} {status?.memory?.unit}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Utilizzo: {status?.memory?.used && status?.memory?.total
                  ? `${Math.round((status.memory.used / status.memory.total) * 100)}%`
                  : 'N/A'}
              </Typography>
            </Box>
          </Grid>

          {/* Node.js Info */}
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">Node.js</Typography>
            </Box>
            <Box pl={4}>
              <Typography variant="body2" color="text.secondary">
                Versione: {status?.node?.version}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Piattaforma: {status?.node?.platform}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {autoRefresh && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Auto-refresh attivo (ogni {refreshInterval / 1000} secondi)
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default SystemStatus;
